import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useMemo, useRef } from 'react'

type TooltipCallbacks = {
    show: () => void
    dismiss: () => void
}

type TooltipProviderContextValue = {
    register(id: string, callbacks: TooltipCallbacks): () => void
    requestOpen(id: string): void
    notifyClose(id: string): void
}

const noopUnregister = () => {}

const fallback: TooltipProviderContextValue = {
    register: () => noopUnregister,
    requestOpen: () => {},
    notifyClose: () => {},
}

const TooltipProviderContext = createContext<TooltipProviderContextValue>(fallback)

type InternalState = {
    callbacks: Record<string, TooltipCallbacks>
    hoverStack: Array<string>
}

export const TooltipProvider = ({ children }: { children: ReactNode }) => {
    const stateRef = useRef<InternalState>({ callbacks: {}, hoverStack: [] })

    const register = useCallback((id: string, callbacks: TooltipCallbacks) => {
        stateRef.current.callbacks[id] = callbacks

        return () => {
            delete stateRef.current.callbacks[id]
            stateRef.current.hoverStack = stateRef.current.hoverStack.filter((x) => x !== id)
        }
    }, [])

    const requestOpen = useCallback((id: string) => {
        const { callbacks, hoverStack } = stateRef.current
        const top = hoverStack[hoverStack.length - 1]

        if (top && top !== id) {
            callbacks[top]?.dismiss()
        }

        stateRef.current.hoverStack = [...hoverStack.filter((x) => x !== id), id]
    }, [])

    const notifyClose = useCallback((id: string) => {
        const { callbacks, hoverStack } = stateRef.current

        stateRef.current.hoverStack = hoverStack.filter((x) => x !== id)

        const newTop = stateRef.current.hoverStack[stateRef.current.hoverStack.length - 1]

        if (newTop) {
            callbacks[newTop]?.show()
        }
    }, [])

    const value = useMemo(() => ({ register, requestOpen, notifyClose }), [register, requestOpen, notifyClose])

    return <TooltipProviderContext.Provider value={value}>{children}</TooltipProviderContext.Provider>
}

export const useTooltipProvider = () => useContext(TooltipProviderContext)
